import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Bell, MapPin, TrendingUp, Mail, Check, Loader2, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const SITE = "https://lagooncityrealestate.ca";

const PERKS = [
  { icon: Bell, title: "Know first", text: "Get a heads-up the moment a home near you is listed — before it hits the wider market." },
  { icon: TrendingUp, title: "See the sold price", text: "Find out what neighbouring homes actually sold for, so you know where your street stands." },
  { icon: MapPin, title: "Hyper-local to your canal", text: "Updates tuned to Lagoon City and your specific street or waterway — not generic city data." },
];

export default function NeighbourhoodBuzzer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.address.trim()) return;
    setStatus("sending");
    try {
      await api.post("/neighbourhood-buzzer", form);
      trackEvent("generate_lead", { method: "neighbourhood_buzzer" });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const canonical = `${SITE}/neighbourhood-buzzer`;
  const title = "Neighbourhood Buzzer — Free Alerts When Lagoon City Neighbours Sell | Joselle Alves";
  const desc =
    "Get free, instant updates whenever a home near you is listed or sold in Lagoon City, Ontario. Enter your address and Joselle Alves will keep you posted on your street's waterfront market.";

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta
          name="keywords"
          content="Lagoon City home alerts, neighbourhood buzzer, when are my neighbours selling, Lagoon City sold prices, Lagoon City real estate updates, home value alerts Ontario"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE}/assets/community/canals-aerial.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <PageHeader />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="/assets/community/canals-aerial.jpg"
            alt="Aerial view of the Lagoon City canals"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E] via-[#0D0B0E]/85 to-[#0D0B0E]/60" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16 sm:pt-28 sm:pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-4 flex items-center gap-2">
              <Bell size={14} /> Neighbourhood Buzzer
            </p>
            <h1
              data-testid="buzzer-title"
              className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight"
            >
              Instant updates when your <em className="italic text-[#E890A7]">neighbours</em> are selling — or sold
            </h1>
            <p className="mt-6 max-w-xl text-slate-300 font-light text-lg">
              Enter your address and get <strong className="text-slate-100 font-medium">free</strong> notifications
              whenever a home near you is listed — plus what it actually sold for. No cost, no
              obligation, unsubscribe anytime.
            </p>
          </motion.div>

          {/* Signup card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="rounded-[2rem] border border-[#E890A7]/25 bg-[#151218]/90 backdrop-blur-xl p-8 sm:p-10"
          >
            {status === "done" ? (
              <div className="text-center py-8" data-testid="buzzer-success">
                <span className="grid place-items-center h-16 w-16 rounded-full bg-[#E890A7]/15 border border-[#E890A7]/30 mx-auto mb-6">
                  <Check size={30} className="text-[#E890A7]" />
                </span>
                <h2 className="font-display text-3xl">You're on the list</h2>
                <p className="mt-3 text-slate-400 font-light">
                  We'll email you the moment there's activity near <span className="text-[#F2A7BA]">{form.address}</span>.
                  A confirmation is on its way to {form.email}.
                </p>
                <Link
                  to="/"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 hover:bg-[#F2A7BA] transition-colors"
                >
                  Back to home <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="buzzer-form">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7] mb-2 flex items-center gap-2">
                  <Mail size={13} /> Free — get your first update
                </p>
                <h2 className="font-display font-light text-2xl sm:text-3xl leading-tight mb-6">
                  Watch my street
                </h2>
                <div className="space-y-4">
                  <input
                    data-testid="buzzer-name"
                    value={form.name}
                    onChange={set("name")}
                    required
                    placeholder="Your name"
                    className="w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none focus:border-[#E890A7]/60 transition-colors"
                  />
                  <input
                    data-testid="buzzer-address"
                    value={form.address}
                    onChange={set("address")}
                    required
                    placeholder="Your street or address"
                    className="w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none focus:border-[#E890A7]/60 transition-colors"
                  />
                  <input
                    data-testid="buzzer-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    required
                    placeholder="Your email"
                    className="w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none focus:border-[#E890A7]/60 transition-colors"
                  />
                  <input
                    data-testid="buzzer-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone (optional)"
                    className="w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none focus:border-[#E890A7]/60 transition-colors"
                  />
                </div>
                {status === "error" && (
                  <p data-testid="buzzer-error" className="mt-4 text-sm text-red-400">
                    Couldn't sign you up just now — please try again.
                  </p>
                )}
                <button
                  data-testid="buzzer-submit"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-6 py-3.5 hover:bg-[#F2A7BA] transition-colors disabled:opacity-60"
                >
                  {status === "sending" && <Loader2 size={16} className="animate-spin" />}
                  {status === "sending" ? "Signing you up…" : "Get free updates"}
                </button>
                <p className="mt-4 text-xs text-slate-500 font-light text-center">
                  Your details stay private. Unsubscribe anytime.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {PERKS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`buzzer-perk-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-3xl border border-[#E890A7]/15 bg-[#151218]/60 p-7 hover:border-[#E890A7]/40 transition-colors duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#E890A7]/12 border border-[#E890A7]/25 mb-5">
                  <Icon size={22} className="text-[#E890A7]" />
                </span>
                <h3 className="font-display text-xl">{p.title}</h3>
                <p className="mt-2 text-slate-400 font-light text-sm leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
